import { Grid, Menu } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import * as kapi from "api/Knowledge";
import { DefaultListParameters } from "assets/appConfig";
import { GlobalClassKeys, TimelineStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdAuthor, CmdAvailableEndDate, CmdAvailableStartDate, CmdCourseExpired, CmdPublishEndDate, CmdPublishStartDate, CmdWithAutoEnroll, CmdWithAward, CmdWithoutAutoEnroll, CmdWithoutAward, CommandLabels } from "components/AdvFilter/CommonCommands";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonOrderBy from "components/Buttons/IconButtonOrderby";
import IconButtonSubTopic from "components/Buttons/IconButtonSubTopic";
import { RequestIndicator } from "components/CallServer";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import GenericListSelection, { IGenericListSelectionProps } from "components/GenericListSelection";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { PublicationType, SystemRoles } from "models/ApiEnums";
import { CourseAdminListFilter, CourseAdminListOrderBy, GetCourseAdminListParams, PagedResult, PublicationAdminCourseDto, SavePublicationResult, TopicSuggestionDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import Session from "services/Session";
import CourseAdminCard from "views/Publication/Components/CourseAdminCard";
import CourseAdminEdit from "views/Publication/Components/CourseAdminEdit";
import { publicationFactory } from "views/Timeline/Components/FeedHelpers";
import TopicAutoComplete from "views/Topic/Components/TopicAutoComplete";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IDataState {
    advFilters: AdvFilterResult[];
    filter: CourseAdminListFilter;
    orderBy: CourseAdminListOrderBy;
    offset: number;
}

interface IState extends IDataState {
    list: PagedResult<PublicationAdminCourseDto>;
    formCycle: RequestLifeCycle;
    lastError: any;
    selectedTopic: TopicSuggestionDto[];

    dialog: any;
    orderByMenuAnchorEl: any;
    editCourseId: string;
}

interface IProps {
}

type Props = IProps & WithStyles<typeof TimelineStyleClasses> & RouteComponentProps<any, any, any> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class CourseAdminList extends React.Component<Props, IState> {

    private advFilterRef: AdvFilterComponent;
    private isFiltered: boolean = false;

    constructor(props: Props) {
        super(props);

        let selectedTopic: TopicSuggestionDto[] = [];

        if (this.props.location.state && this.props.location.state.topic) {
            var t = this.props.location.state.topic as AutoCompleteDataModel;
            selectedTopic = [{
                key: t.key,
                label: t.label,
                thumbnailUrl: t.thumbnailUrl,
                canDelete: true,
            }];
        }

        this.state = {
            advFilters: [],
            selectedTopic: selectedTopic == null ? [] : selectedTopic,
            filter: this.resetFilter(selectedTopic),
            orderBy: CourseAdminListOrderBy.Title,

            formCycle: RequestLifeCycle.None,
            lastError: null,
            list: { items: null, total: 0 },
            offset: 0,
            orderByMenuAnchorEl: null,

            dialog: null,
            editCourseId: props.location.state && props.location.state.editCourseId ? props.location.state.editCourseId : null
        };
    }

    ///
    private updateHistory() {
        this.props.history.replace(this.props.location.pathname,
            {
                advFilters: this.state.advFilters,
                filter: this.state.filter,
                orderBy: this.state.orderBy,
                offset: this.state.offset,
                selectedTopic: this.state.selectedTopic
            } as IDataState
        );
    }

    ///
    private loadData(offset: number) {

        const { filter, orderBy } = this.state;

        const p: GetCourseAdminListParams = {
            filter: filter,
            orderBy: orderBy,
            range: { offset: offset, take: DefaultListParameters.viewRecordsByPage }
        };

        this.setState({ formCycle: RequestLifeCycle.Loading, orderByMenuAnchorEl: null, dialog: null });

        api.getCoursePublicationAdminList(p)
            .then((result) => {
                this.setState({ list: result, formCycle: RequestLifeCycle.None, offset: offset }, () => this.updateHistory());
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, formCycle: RequestLifeCycle.LoadFailed, offset: 0 });
            });
    }

    ///
    private handleActionClose = (result: SavePublicationResult) => {
        this.setState({
            dialog: null,
            editCourseId: result ? result.publicationId : null
        });
    };

    ///
    private buildCreatePublicationMenu() {
        return (
            <IconButtonAdd
                title={this.props.t("Common.Buttons.NewCourseText")}
                color="inherit"
                onClick={(e) => this.handleAdd()}
            />
        );
    }

    //#region FILTER

    ///
    private get commands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdWithAutoEnroll, displayName: cmdLabels[CmdWithAutoEnroll], group: 1, optionGroup: 1 },
            { keyword: CmdWithoutAutoEnroll, displayName: cmdLabels[CmdWithoutAutoEnroll], group: 1, optionGroup: 1 },
            { keyword: CmdWithAward, displayName: cmdLabels[CmdWithAward], group: 1, optionGroup: 2 },
            { keyword: CmdWithoutAward, displayName: cmdLabels[CmdWithoutAward], group: 1, optionGroup: 2 },
            { keyword: CmdPublishStartDate, displayName: cmdLabels[CmdPublishStartDate], picker: AdvFilterPicker.Calendar, group: 2 },
            { keyword: CmdPublishEndDate, displayName: cmdLabels[CmdPublishEndDate], picker: AdvFilterPicker.Calendar, group: 2 },
            { keyword: CmdAvailableStartDate, displayName: cmdLabels[CmdAvailableStartDate], picker: AdvFilterPicker.Calendar, group: 3 },
            { keyword: CmdAvailableEndDate, displayName: cmdLabels[CmdAvailableEndDate], picker: AdvFilterPicker.Calendar, group: 3 },
            {
                keyword: CmdAuthor,
                displayName: cmdLabels[CmdAuthor],
                canRepeat: true,
                group: 4,
                picker: AdvFilterPicker.Custom,
                customPicker: (p: IGenericListSelectionProps): React.ReactElement => {
                    return (
                        <GenericListSelection
                            title="Autores de Cursos"
                            showThumbnail={true}
                            onGetData={(searchText, offset, take) => kapi.courseAuthorsSelection(searchText, offset, take)}
                            {...p}
                        />
                    );
                },
            },
            { keyword: CmdCourseExpired, displayName: cmdLabels[CmdCourseExpired], group: 5 },
        ];
    }

    ///
    private buildOrderByMenu() {
        const { orderBy } = this.state;
        return (
            <>
                <IconButtonOrderBy
                    color="inherit"
                    onClick={
                        (e) => {
                            this.setState({ orderByMenuAnchorEl: e.currentTarget });
                        }
                    }
                />
                <Menu
                    anchorEl={this.state.orderByMenuAnchorEl}
                    open={this.state.orderByMenuAnchorEl != null}
                    onClose={
                        () => {
                            this.setState({ orderByMenuAnchorEl: null });
                        }}
                >
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Title")} value={CourseAdminListOrderBy.Title} currentSelection={orderBy} onClick={this.applyOrder} />
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.CreationDate")} value={CourseAdminListOrderBy.Creation} currentSelection={orderBy} onClick={this.applyOrder} />
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostSubscribed")} value={CourseAdminListOrderBy.MostEnrolls} currentSelection={orderBy} onClick={this.applyOrder} />
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostLiked")} value={CourseAdminListOrderBy.MostLikes} currentSelection={orderBy} onClick={this.applyOrder} />
                    <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostCommented")} value={CourseAdminListOrderBy.MostComments} currentSelection={orderBy} onClick={this.applyOrder} />
                </Menu>
            </>
        );
    }

    ///
    private handleShowSubTopics = () => {
        this.setState(
            (state) => {
                state.filter.showSubtopics = !state.filter.showSubtopics;
                return state;
            },
            () => this.loadData(0)
        );
    };

    ///
    private resetFilter(selectedTopic: AutoCompleteDataModel[]): CourseAdminListFilter {
        this.isFiltered = false;

        return {
            topicId: selectedTopic.length == 0 ? null : selectedTopic[0].key,
            showSubtopics: this.state == null ? false : this.state.filter.showSubtopics,
            searchText: null,
            creationStartDate: null,
            creationEndDate: null,
            availableStartDate: null,
            availableEndDate: null,
            authors: null,
            withAutoEnroll: false,
            withAward: false,
            withoutAutoEnroll: false,
            withoutAward: false,
            expiredOnly: false
        };
    }

    ///
    private buildFilter(searchText: string, advFilters: AdvFilterResult[]): Partial<CourseAdminListFilter> {
        this.isFiltered = true;

        const dtStartDate = advFilters.find(x => x.keyword == CmdPublishStartDate);
        const dtEndDate = advFilters.find(x => x.keyword == CmdPublishEndDate);
        const dtAvailableStartDate = advFilters.find(x => x.keyword == CmdAvailableStartDate);
        const dtAvailableEndDate = advFilters.find(x => x.keyword == CmdAvailableEndDate);

        return {
            searchText: searchText.length == 0 ? null : searchText,

            authors: advFilters.filter(x => x.keyword == CmdAuthor).map(v => v.value),

            expiredOnly: advFilters.findIndex(x => x.keyword == CmdCourseExpired) > -1,

            withAutoEnroll: advFilters.findIndex(x => x.keyword == CmdWithAutoEnroll) > -1,
            withoutAutoEnroll: advFilters.findIndex(x => x.keyword == CmdWithoutAutoEnroll) > -1,
            withAward: advFilters.findIndex(x => x.keyword == CmdWithAward) > -1,
            withoutAward: advFilters.findIndex(x => x.keyword == CmdWithoutAward) > -1,

            creationStartDate: dtStartDate ? (dtStartDate.value as Date).putStartOfDay() : null,
            creationEndDate: dtEndDate ? (dtEndDate.value as Date).putEndOfDay() : null,

            availableStartDate: dtAvailableStartDate ? (dtAvailableStartDate.value as Date).putStartOfDay() : null,
            availableEndDate: dtAvailableEndDate ? (dtAvailableEndDate.value as Date).putEndOfDay() : null
        };
    }

    ///
    private applyOrder = (orderBy: CourseAdminListOrderBy) => {
        this.setState({ orderBy }, () => this.loadData(0));
    };

    ///
    private updateAdvFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        const newFilter = (searchText.length == 0 && advFilters.length == 0)
            ? this.resetFilter(this.state.selectedTopic)
            : this.buildFilter(searchText, advFilters);

        this.setState(
            {
                filter: { ...this.state.filter, ...newFilter },
                advFilters
            }, () => this.loadData(0)
        );
    };

    ///
    private updateTopicSelection = (items: TopicSuggestionDto[]) => {
        this.setState((state) => {
            state.filter.topicId = items.length == 0 ? null : items[0].key;
            return { ...state, selectedTopic: items, };
        }, () => this.loadData(0));
    };

    ///
    private renderAdvFilter(): JSX.Element {
        const { selectedTopic, filter } = this.state;

        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <MuiFilterLine>
                        <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                            <Grid item>
                                {this.buildCreatePublicationMenu()}
                            </Grid>
                            <Grid item className={GlobalClassKeys.grow}>
                                <TopicAutoComplete
                                    id="topicFilter"
                                    placeholder={this.props.t("Views.Publication.CourseAdminList.TopicPlaceholder")}
                                    margin={"normal"}
                                    multiple={false}
                                    showAvatar={false}
                                    chipColor="info"
                                    selectedItems={selectedTopic}
                                    onChange={this.updateTopicSelection}
                                />
                            </Grid>

                            {
                                selectedTopic.length > 0 &&
                                <Grid item>
                                    <IconButtonSubTopic
                                        color={filter.showSubtopics ? "warning" : "inherit"}
                                        onClick={this.handleShowSubTopics}
                                    />
                                </Grid>
                            }
                            <Grid item>
                                {this.buildOrderByMenu()}
                            </Grid>
                        </Grid>
                    </MuiFilterLine>

                    <MuiFilterAdvancedContainer disableMargins>
                        <AdvFilter
                            id="advfilter"
                            commands={this.commands}
                            innerRef={(ref) => this.advFilterRef = ref}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            selectedCommands={this.state.advFilters}
                            onUpdateSelection={this.updateAdvFilter} />
                    </MuiFilterAdvancedContainer>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    //#endregion

    ///
    private handleAdd() {
        this.setState({
            dialog:
                publicationFactory(null, PublicationType.Course, this.handleActionClose)
        });
    }

    ///
    private handleEdit(item: PublicationAdminCourseDto) {

        this.setState({
            editCourseId: item.publicationId
        });
    }

    ///
    private handleDelete = (publicationId: string, enrollments: number) => {
        this.setState({
            dialog: <MuiConfirmation
                title={this.props.t("Views.Publication.CourseAdminList.ConfirmationDeleteTitle")}
                message={
                    enrollments > 0
                    ? this.props.t("Views.Publication.CourseAdminList.ConfirmationDeleteWithEnrollMessage")
                    : this.props.t("Views.Publication.CourseAdminList.ConfirmationDeleteMessage")
                }
                onClose={
                    (result) => {
                        if (result) {

                            this.setState({ dialog: null, formCycle: RequestLifeCycle.Posting });

                            kapi.deletePublication(publicationId, PublicationType.Course)
                                .then((result) => {
                                    if (result) this.loadData(this.state.offset);
                                    else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                })
                                .catch((error) => {
                                    this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                });
                        }
                        else {
                            this.setState({ dialog: null });
                        }
                    }
                }
            />
        });
    };

    ///
    private handleExpand = (item: PublicationAdminCourseDto, textExpanded: boolean) => {

        const d = { ...item };

        var newItems = this.state.list.items.slice(0);
        var pub = newItems.find(p => p.publicationId == item.publicationId);
        if (pub == null) return;

        if (textExpanded && d.body.fullContent == null) {

            api.getPublicationAdminBody(item.publicationId)
                .then((body) => {
                    pub.body = body;
                    this.setState({
                        list: {
                            items: newItems,
                            total: this.state.list.total
                        }
                    });
                });
        }
        else {
            pub.body.fullContentLoaded = textExpanded;
            this.setState({
                list: {
                    items: newItems,
                    total: this.state.list.total
                }
            });
        }
    };

    ///
    private renderItems() {
        const { list } = this.state;

        if (list.total == 0 && !this.isFiltered)
            return Session.hasRole(SystemRoles.CourseCreator) && !Session.hasRole(SystemRoles.CourseManager)
                ? <MuiMessageBox variant="info" message={this.props.t("Views.Publication.CourseAdminList.EmptyDataForCourseCreator")} />
                : <MuiMessageBox variant="info" message={this.props.t("Common.Feed.Filter.CourseResultText_zero")} />;


        if (list.total == 0 && this.isFiltered)
            return (
                <MuiMessageBox
                    variant="warning"
                    action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                    message={this.props.t("Common.Feed.Filter.CourseFilteredResultText_zero")}
                />
            );

        return (
            <MuiContainer margin="normal">
                <MuiContainer margin="normal">
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs>
                            <CountFilter
                                count={list.total}
                                filtered={this.isFiltered}
                                resource="Common.Feed.Filter.CourseResultText"
                                filteredResource="Common.Feed.Filter.CourseFilteredResultText"
                                onResetFilter={() => this.advFilterRef.resetFilter()}
                            />
                        </Grid>
                        {
                            Session.hasRole(SystemRoles.CourseCreator) && !Session.hasRole(SystemRoles.CourseManager) &&
                            <Grid item>
                                <MuiWarning showIcon>{this.props.t("Common.Feed.Filter.CourseCreatorListInfo")}</MuiWarning>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>

                {
                    this.state.list.items.map((item, index) => (
                        <MuiContainer margin="dense" key={index}>
                            <CourseAdminCard
                                item={item}
                                allowManageEnroll
                                fullView={false}
                                onDelete={this.handleDelete}
                                onEdit={() => this.handleEdit(item)}
                                onExpand={this.handleExpand}
                                onNeedReload={() => this.loadData(this.state.offset)}
                            />

                        </MuiContainer>
                    ))
                }
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.list.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { formCycle, lastError } = this.state;

        if (formCycle == RequestLifeCycle.LoadFailed)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                <RequestIndicator
                    open={formCycle == RequestLifeCycle.Posting}
                    type="post"
                />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                <MuiContainer margin="normal">
                    {this.renderAdvFilter()}
                </MuiContainer>

                {
                    formCycle == RequestLifeCycle.Loading
                        ? <LoadingIndicator />
                        : this.renderItems()
                }
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {
        const { editCourseId } = this.state;
        return (
            <div className={this.props.classes.timelineContainer}>
                {this.state.dialog}

                {
                    editCourseId
                        ? (
                            <CourseAdminEdit
                                courseId={editCourseId}
                                onClose={
                                    (reload) => {
                                        this.setState({ editCourseId: null }, () => {
                                            if (reload) this.loadData(this.state.offset);
                                        });
                                    }
                                }
                            />
                        )
                        : this.renderList()
                }
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export default withTranslation()(withStyles(TimelineStyleClasses)(withSnackbar(CourseAdminList)));